import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

/**
 * Genera un PDF con le informazioni della partita
 * @param {Object} matchData - Dati della partita
 * @param {Array} teamPlayers - Lista dei giocatori della squadra
 * @param {Function} t - Funzione di traduzione
 */
export const generateMatchPDF = (matchData, teamPlayers, t) => {
	if (!jsPDF) {
		throw new Error(
			"jsPDF non è installato. Per utilizzare questa funzionalità, installa jsPDF eseguendo: npm install jspdf",
		);
	}

	// =========================
	// Document setup (A4, pt)
	// =========================
	const doc = new jsPDF({unit: "pt", format: "a4"});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	// =========================
	// Spacing system
	// =========================
	const SP = {
		pagePad: 48,
		blockGap: 14,
		cardPad: 14,
		footerH: 50,
	};

	let yPosition = SP.pagePad;

	// =========================
	// Theme
	// =========================
	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
		success: [34, 197, 94],
		warn: [251, 191, 36],
		danger: [239, 68, 68],
		yellow: [251, 191, 36],
		red: [239, 68, 68],
	};

	// =========================
	// Page break
	// =========================
	const checkPageBreak = (requiredHeight) => {
		if (yPosition + requiredHeight > pageHeight - SP.footerH) {
			doc.addPage();
			yPosition = SP.pagePad;
			return true;
		}
		return false;
	};

	// =========================
	// Helpers
	// =========================
	const safeText = (v, fallback = "---") =>
		v === null || v === undefined || v === "" ? fallback : String(v);

	const setOpacity = (opacity) => {
		// Works on newer jsPDF; fail-safe otherwise
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity}));
		} catch (_) {
			/* no-op */
		}
	};

	const resetOpacity = () => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity: 1}));
		} catch (_) {
			/* no-op */
		}
	};

	const ellipsize = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		const s = safeText(text, "");
		if (!s) return "";
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		if (doc.getTextWidth(s) <= maxWidth) return s;

		const ell = "…";
		let lo = 0;
		let hi = s.length;
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			const candidate = s.slice(0, mid) + ell;
			if (doc.getTextWidth(candidate) <= maxWidth) lo = mid + 1;
			else hi = mid;
		}
		return s.slice(0, Math.max(0, lo - 1)) + ell;
	};

	const wrapText = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		return doc.splitTextToSize(safeText(text, ""), maxWidth);
	};

	const drawCard = (
		x,
		y,
		w,
		h,
		{
			radius = 12,
			fill = [255, 255, 255],
			border = theme.border,
			shadow = true,
		} = {},
	) => {
		if (shadow) {
			// soft shadow (fake)
			doc.setFillColor(0, 0, 0);
			setOpacity(0.06);
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			resetOpacity();
		}

		doc.setDrawColor(...border);
		doc.setLineWidth(0.8);
		doc.setFillColor(...fill);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	const drawSectionHeader = (title, {dotColor = theme.primary} = {}) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		checkPageBreak(38);

		doc.setFillColor(...theme.primary);
		doc.roundedRect(x, yPosition, w, 22, 10, 10, "F");

		doc.setFillColor(...dotColor);
		doc.circle(x + 12, yPosition + 11, 4, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(12);
		doc.setTextColor(255, 255, 255);
		doc.text(title, x + 22, yPosition + 15);

		yPosition += 22 + 10;
		doc.setTextColor(...theme.text);
	};

	const drawKeyValueTable = (items) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		const labelW = 150;
		const valueW = w - SP.cardPad * 2 - labelW - 10;

		const rows = (items || []).map((it) => {
			const lines = wrapText(it.value || "---", valueW, 11, [
				"helvetica",
				"normal",
			]);
			const rowH = Math.max(18, lines.length * 14);
			return {...it, lines, rowH};
		});

		const h = SP.cardPad * 2 + rows.reduce((sum, r) => sum + r.rowH, 0);

		checkPageBreak(h + 6);

		drawCard(x, yPosition, w, h, {radius: 12, shadow: true});

		let y = yPosition + SP.cardPad + 12;

		rows.forEach((r, idx) => {
			// zebra
			if (idx % 2 === 1) {
				doc.setFillColor(...theme.bg);
				doc.roundedRect(x + 8, y - 12, w - 16, r.rowH, 8, 8, "F");
			}

			doc.setFont("helvetica", "bold");
			doc.setFontSize(10);
			doc.setTextColor(...theme.muted);
			doc.text(`${safeText(r.label, "")}:`, x + SP.cardPad, y);

			doc.setFont("helvetica", "normal");
			doc.setFontSize(11);
			doc.setTextColor(...theme.text);
			doc.text(r.lines, x + SP.cardPad + labelW, y);

			y += r.rowH;
		});

		yPosition += h + SP.blockGap;
		doc.setTextColor(...theme.text);
	};

	const drawSubSectionLabel = (label) => {
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		checkPageBreak(18);

		doc.setFillColor(...theme.bg);
		doc.roundedRect(x, yPosition, w, 18, 9, 9, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.primaryDark);
		doc.text(label, x + 10, yPosition + 13);

		yPosition += 18 + 8;
		doc.setTextColor(...theme.text);
	};

	const drawListCard = ({
		title,
		items,
		dotColor = theme.primary,
		itemRenderer,
		countBadge = true,
		rowHeight = 18,
	}) => {
		if (!items || items.length === 0) return;

		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		// Title header (pill)
		const headerH = 22;
		const headerGap = 10;

		const availableH =
			pageHeight -
			SP.footerH -
			SP.pagePad -
			(headerH + headerGap) -
			SP.cardPad * 2;
		const maxRowsPerPage = Math.max(1, Math.floor(availableH / rowHeight));

		let idx = 0;

		while (idx < items.length) {
			const rowsThisPage = Math.min(maxRowsPerPage, items.length - idx);

			const cardH = SP.cardPad * 2 + rowsThisPage * rowHeight;

			checkPageBreak(headerH + headerGap + cardH + SP.blockGap);

			// Header
			doc.setFillColor(...theme.primary);
			doc.roundedRect(x, yPosition, w, headerH, 10, 10, "F");

			doc.setFillColor(...dotColor);
			doc.circle(x + 12, yPosition + 11, 4, "F");

			doc.setFont("helvetica", "bold");
			doc.setFontSize(12);
			doc.setTextColor(255, 255, 255);

			const headerText = countBadge ? `${title} (${items.length})` : title;
			doc.text(headerText, x + 22, yPosition + 15);

			yPosition += headerH + headerGap;

			// Card
			drawCard(x, yPosition, w, cardH, {radius: 12, shadow: true});

			// Content
			let y = yPosition + SP.cardPad + 12;

			for (let r = 0; r < rowsThisPage; r++) {
				const item = items[idx + r];

				// zebra
				if (r % 2 === 1) {
					doc.setFillColor(...theme.bg);
					doc.roundedRect(x + 8, y - 12, w - 16, rowHeight, 8, 8, "F");
				}

				if (itemRenderer) itemRenderer({item, x, y, w, rowHeight});
				else {
					doc.setFillColor(...dotColor);
					doc.circle(x + 12, y - 2, 3, "F");

					doc.setFont("helvetica", "normal");
					doc.setFontSize(11);
					doc.setTextColor(...theme.text);
					const name =
						`${safeText(item?.firstName, "")} ${safeText(item?.lastName, "")}`.trim();
					doc.text(
						ellipsize(name, w - 60, 11, ["helvetica", "normal"]),
						x + 24,
						y,
					);
				}

				y += rowHeight;
			}

			yPosition += cardH + SP.blockGap;

			idx += rowsThisPage;
			doc.setTextColor(...theme.text);
		}
	};

	// =========================
	// Data normalized
	// =========================
	const homeClub = safeText(matchData?.homeClub);
	const awayClub = safeText(matchData?.awayClub);

	const hasResult =
		Number.isInteger(matchData?.homeClubGoals) &&
		Number.isInteger(matchData?.awayClubGoals);
	const result = hasResult
		? `${matchData.homeClubGoals} - ${matchData.awayClubGoals}`
		: null;

	// =========================
	// HEADER (premium)
	// =========================
	const headerH = 92;

	doc.setFillColor(...theme.primaryDark);
	doc.rect(0, 0, pageWidth, headerH, "F");

	doc.setFillColor(...theme.primary);
	doc.rect(0, headerH - 20, pageWidth, 20, "F");

	doc.setFillColor(...theme.primaryLight);
	doc.rect(0, headerH - 3, pageWidth, 3, "F");

	doc.setFont("helvetica", "bold");
	doc.setFontSize(22);
	doc.setTextColor(255, 255, 255);
	doc.text("REPORT PARTITA", SP.pagePad, 46);

	const dateText = matchData?.startDate
		? moment(matchData.startDate).format("DD MMMM YYYY")
		: "";
	const catText = safeText(matchData?.playersCategory, "");
	const subtitle = [dateText, catText].filter(Boolean).join(" • ");

	doc.setFont("helvetica", "normal");
	doc.setFontSize(10);
	doc.setTextColor(235, 245, 255);
	doc.text(subtitle, SP.pagePad, 66);

	// watermark soft
	setOpacity(0.06);
	doc.setFont("helvetica", "bold");
	doc.setFontSize(44);
	doc.setTextColor(255, 255, 255);
	doc.text("REPORT", pageWidth - SP.pagePad, 56, {align: "right"});
	resetOpacity();

	yPosition = headerH + 22;
	doc.setTextColor(...theme.text);

	// =========================
	// HERO RESULT CARD
	// =========================
	{
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const h = 76;

		checkPageBreak(h + SP.blockGap);

		drawCard(x, yPosition, w, h, {radius: 14, shadow: true});

		doc.setFont("helvetica", "bold");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text("RISULTATO", x + SP.cardPad, yPosition + 18);

		const contentY = yPosition + 50;

		const colW = w - SP.cardPad * 2;
		const sideW = Math.floor(colW * 0.38);
		const midW = colW - sideW * 2;

		const homeX = x + SP.cardPad;
		const midX = homeX + sideW;
		const awayX = midX + midW;

		// HOME
		doc.setFont("helvetica", "bold");
		doc.setFontSize(14);
		doc.setTextColor(...theme.text);
		doc.text(
			ellipsize(homeClub, sideW - 6, 14, ["helvetica", "bold"]),
			homeX,
			contentY,
		);

		// MID
		if (result) {
			doc.setFont("helvetica", "bold");
			doc.setFontSize(26);
			doc.setTextColor(...theme.primary);
			doc.text(result, midX + midW / 2, contentY + 2, {align: "center"});
		} else {
			doc.setFont("helvetica", "normal");
			doc.setFontSize(12);
			doc.setTextColor(...theme.muted);
			doc.text("VS", midX + midW / 2, contentY, {align: "center"});
		}

		// AWAY
		doc.setFont("helvetica", "bold");
		doc.setFontSize(14);
		doc.setTextColor(...theme.text);
		doc.text(
			ellipsize(awayClub, sideW - 6, 14, ["helvetica", "bold"]),
			awayX + sideW,
			contentY,
			{align: "right"},
		);

		// subtle dividers
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.line(midX, yPosition + 28, midX, yPosition + h - 16);
		doc.line(awayX, yPosition + 28, awayX, yPosition + h - 16);

		yPosition += h + SP.blockGap;
		doc.setTextColor(...theme.text);
	}

	// =========================
	// INFO PRINCIPALI
	// =========================
	drawSectionHeader("Informazioni Principali");

	const mainInfo = [
		{
			label: t?.("date_label") || "Data",
			value: matchData?.startDate
				? moment(matchData.startDate).format("DD/MM/YYYY")
				: "---",
		},
		{
			label: t?.("time_label") || "Orario",
			value: matchData?.startDate
				? moment(matchData.startDate).format("HH:mm") +
					(matchData?.endDate
						? " - " + moment(matchData.endDate).format("HH:mm")
						: "")
				: "---",
		},
		{
			label: t?.("category") || "Categoria",
			value: safeText(matchData?.playersCategory),
		},
		{label: "Tipo Partita", value: safeText(matchData?.matchType)},
		{label: "Stadio", value: safeText(matchData?.fieldName)},
		{label: "Turno", value: safeText(matchData?.round)},
	];

	drawKeyValueTable(mainInfo);

	// =========================
	// PLAYERS LISTS (Convocati / Assenti / Infortunati)
	// =========================
	const convocations = matchData?.matchConvocations || [];
	const players = teamPlayers || [];

	const resolvePlayer = (playerId) => players.find((p) => p?.id === playerId);

	const sortByLastName = (a, b) =>
		(a?.lastName || "").localeCompare(b?.lastName || "");

	const convocated = convocations
		.filter((c) => c?.status === "Presente")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	const absents = convocations
		.filter((c) => c?.status === "Assente")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	const injured = convocations
		.filter((c) => c?.status === "Infortunato")
		.map((c) => resolvePlayer(c?.playerId))
		.filter(Boolean)
		.sort(sortByLastName);

	drawListCard({
		title: "Convocati",
		items: convocated,
		dotColor: theme.success,
		rowHeight: 18,
	});

	drawListCard({
		title: "Assenti",
		items: absents,
		dotColor: theme.muted,
		rowHeight: 18,
	});

	drawListCard({
		title: "Infortunati",
		items: injured,
		dotColor: theme.warn,
		rowHeight: 18,
	});

	// =========================
	// GOALS (Marcatori)
	// =========================
	const goals = (matchData?.matchEvents || []).filter(
		(e) => e?.type === "goal",
	);

	if (goals.length > 0) {
		drawSectionHeader("Marcatori", {dotColor: theme.success});

		const homeGoals = goals.filter((g) => g?.clubId === matchData?.homeClubId);
		const awayGoals = goals.filter((g) => g?.clubId === matchData?.awayClubId);

		const renderGoalsBlock = (clubName, clubGoals) => {
			if (!clubGoals || clubGoals.length === 0) return;

			drawSubSectionLabel(`${clubName}`);

			const x = SP.pagePad;
			const w = pageWidth - SP.pagePad * 2;

			const rowH = 20;

			const sorted = [...clubGoals].sort(
				(a, b) => (a?.minute || 0) - (b?.minute || 0),
			);

			drawListCard({
				title: "Elenco marcatori",
				items: sorted,
				dotColor: theme.success,
				countBadge: false,
				rowHeight: rowH,
				itemRenderer: ({item, x, y, w}) => {
					// icon
					doc.setFillColor(...theme.success);
					doc.circle(x + 12, y - 2, 3, "F");

					const playerName = item?.player
						? `${safeText(item.player.firstName, "")} ${safeText(item.player.lastName, "")}`.trim()
						: "---";

					const minute = item?.minute ? `${item.minute}'` : "";
					const fraction = item?.fraction ? ` (${item.fraction}T)` : "";
					const right = `${minute}${fraction}`.trim();

					doc.setFont("helvetica", "normal");
					doc.setFontSize(11);
					doc.setTextColor(...theme.text);
					doc.text(
						ellipsize(playerName, w - 150, 11, ["helvetica", "normal"]),
						x + 24,
						y,
					);

					doc.setFont("helvetica", "normal");
					doc.setFontSize(10);
					doc.setTextColor(...theme.muted);
					doc.text(right || "", x + w - SP.cardPad, y, {align: "right"});
				},
			});
		};

		renderGoalsBlock(homeClub, homeGoals);
		renderGoalsBlock(awayClub, awayGoals);
	}

	// =========================
	// SANCTIONS (Sanzioni)
	// =========================
	const sanctions = (matchData?.matchEvents || []).filter(
		(e) => e?.type === "sanction",
	);

	if (sanctions.length > 0) {
		drawSectionHeader("Sanzioni", {dotColor: theme.warn});

		const sanctionLabels = {
			yellow_card: "Cartellino Giallo",
			double_yellow_card: "Doppio Cartellino Giallo",
			red_card: "Cartellino Rosso",
		};

		const homeSanctions = sanctions.filter(
			(s) => s?.clubId === matchData?.homeClubId,
		);
		const awaySanctions = sanctions.filter(
			(s) => s?.clubId === matchData?.awayClubId,
		);

		const renderSanctionsBlock = (clubName, clubSanctions) => {
			if (!clubSanctions || clubSanctions.length === 0) return;

			drawSubSectionLabel(`${clubName}`);

			const sorted = [...clubSanctions].sort(
				(a, b) => (a?.minute || 0) - (b?.minute || 0),
			);

			drawListCard({
				title: "Elenco sanzioni",
				items: sorted,
				dotColor: theme.warn,
				countBadge: false,
				rowHeight: 20,
				itemRenderer: ({item, x, y, w}) => {
					const isRed =
						item?.eventType === "red_card" ||
						item?.eventType === "double_yellow_card";
					const cardColor = isRed ? theme.red : theme.yellow;

					// small card icon
					doc.setFillColor(...cardColor);
					doc.roundedRect(x + 9, y - 10, 6, 12, 2, 2, "F");

					const playerName = item?.player
						? `${safeText(item.player.firstName, "")} ${safeText(item.player.lastName, "")}`.trim()
						: "---";

					const sanctionType =
						sanctionLabels[item?.eventType] || safeText(item?.eventType);
					const leftText = `${playerName} — ${sanctionType}`;

					const minute = item?.minute ? `${item.minute}'` : "";
					const fraction = item?.fraction ? ` (${item.fraction}T)` : "";
					const right = `${minute}${fraction}`.trim();

					doc.setFont("helvetica", "normal");
					doc.setFontSize(11);
					doc.setTextColor(...theme.text);
					doc.text(
						ellipsize(leftText, w - 160, 11, ["helvetica", "normal"]),
						x + 24,
						y,
					);

					doc.setFont("helvetica", "normal");
					doc.setFontSize(10);
					doc.setTextColor(...theme.muted);
					doc.text(right || "", x + w - SP.cardPad, y, {align: "right"});
				},
			});
		};

		renderSanctionsBlock(homeClub, homeSanctions);
		renderSanctionsBlock(awayClub, awaySanctions);
	}

	// =========================
	// FOOTER (clean + consistent)
	// =========================
	const totalPages = doc.internal.pages.length - 1;
	const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);

		// separator line
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(1);
		doc.line(
			SP.pagePad,
			pageHeight - SP.footerH + 18,
			pageWidth - SP.pagePad,
			pageHeight - SP.footerH + 18,
		);

		// left text
		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		doc.text(generatedAt, SP.pagePad, pageHeight - 18);

		// right text
		doc.text(
			`Pagina ${i} / ${totalPages}`,
			pageWidth - SP.pagePad,
			pageHeight - 18,
			{align: "right"},
		);
	}

	// =========================
	// Save
	// =========================
	const safeHome = (homeClub || "Home").replace(/[\\/:*?"<>|]/g, "-");
	const safeAway = (awayClub || "Away").replace(/[\\/:*?"<>|]/g, "-");
	const datePart = matchData?.startDate
		? moment(matchData.startDate).format("DD-MM-YYYY")
		: "N-A";

	const fileName = `Partita_${datePart}_${safeHome}_vs_${safeAway}.pdf`;
	doc.save(fileName);
};
